import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCents } from "@/lib/api";
import { PageHeader, Card, Loader } from "@/components/ui/Primitives";
import {
  Sparkles, ArrowRight, AlertCircle, Send, RefreshCw, TrendingUp, Mail,
  CreditCard, Users2, Loader2, Copy,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Revenue Engine — landlord-facing money-making Smart features.
 * 4 tabs: Auto-Upsell · Lead Agents · Churn Recovery · Smart Insights.
 */
const TABS = [
  { key: "upsell", label: "Auto-Upsell", icon: TrendingUp },
  { key: "leads", label: "Lead Agents", icon: Users2 },
  { key: "churn", label: "Churn Recovery", icon: CreditCard },
  { key: "insights", label: "Smart Insights", icon: Sparkles },
];

export default function RevenueEngine() {
  const [tab, setTab] = useState("upsell");
  return (
    <div data-testid="revenue-engine-page">
      <PageHeader
        title="Revenue Engine"
        subtitle="Make more money from your existing portfolio. Smart suggestions, stale-lead recovery, dunning, and portfolio insights — all powered by your live data."
      />

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`revenue-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                active
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "upsell" && <UpsellTab />}
      {tab === "leads" && <LeadAgentsTab />}
      {tab === "churn" && <ChurnRecoveryTab />}
      {tab === "insights" && <SmartInsightsTab />}
    </div>
  );
}

function UpsellTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/revenue/upsell-suggestions")
      .then((r) => setD(r.data))
      .catch(() => toast.error("Couldn't load suggestions"))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader />;
  if (!d) return null;
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <div data-testid="upsell-content">
      {d.total_upside_cad_month > 0 && (
        <Card className="p-6 mb-5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white" data-testid="upsell-total">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Estimated monthly upside</div>
          <div className="font-display text-4xl font-extrabold mt-2 tabular-nums">
            ${d.total_upside_cad_month.toLocaleString("en-CA")} CAD
          </div>
          <div className="text-xs opacity-90 mt-1">if you action everything below this month</div>
        </Card>
      )}

      <div className="grid gap-3">
        {(d.suggestions || []).length === 0 ? (
          <Card className="p-10 text-center" data-testid="upsell-empty">
            <Sparkles className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <div className="font-display text-lg font-bold text-slate-900">Nothing urgent — you&apos;re optimized</div>
            <p className="text-sm text-slate-500 mt-2">Check back in a week.</p>
          </Card>
        ) : (
          d.suggestions.map((s, i) => (
            <Card key={i} className="p-5" data-testid={`upsell-${s.key}`}>
              <div className="flex items-start gap-4">
                <div className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold rounded-md border flex-none ${tones[s.tone] || tones.emerald}`}>
                  +${(s.upside_cad_month || 0).toLocaleString("en-CA")}/mo
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base font-bold text-slate-900">{s.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{s.why}</div>
                </div>
                <Link to={s.cta_route} data-testid={`upsell-cta-${s.key}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 flex-none">
                  {s.cta_label} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

function LeadAgentsTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/revenue/lead-agents")
      .then((r) => setD(r.data))
      .catch(() => toast.error("Couldn't load leads"))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <div data-testid="lead-agents-content">
      <Card className="p-5 mb-5 bg-sky-50 border-sky-200" data-testid="lead-agents-headline">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white border border-sky-200 text-sky-700 flex items-center justify-center flex-none">
            <Users2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-slate-900">{d.count} stale applicant{d.count === 1 ? "" : "s"}</div>
            <div className="text-sm text-slate-600 mt-0.5">
              Pipeline at risk: <strong>${(d.total_pipeline_cad || 0).toLocaleString("en-CA")} CAD</strong> · One follow-up message converts ~15-25% of these.
            </div>
          </div>
        </div>
      </Card>

      {(d.items || []).length === 0 ? (
        <Card className="p-10 text-center"><div className="text-sm text-slate-500">No stale leads — every applicant has been processed.</div></Card>
      ) : (
        <div className="space-y-3">
          {d.items.map((l, i) => (
            <Card key={i} className="p-5" data-testid={`lead-row-${i}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0">
                  <div className="font-display text-base font-bold text-slate-900 truncate">{l.tenant_name || l.tenant_email}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{l.property_name} · {l.days_stale}d stale</div>
                </div>
                <Link to="/applications" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">
                  Open application <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <DraftMessage text={l.suggested_message} email={l.tenant_email} phone={l.tenant_phone}
                testid={`lead-msg-${i}`} kind="lead_followup"
                subjectHint={`Following up — ${l.property_name || "your application"}`} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ChurnRecoveryTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/revenue/churn-recovery")
      .then((r) => setD(r.data))
      .catch(() => toast.error("Couldn't load churn data"))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader />;
  if (!d) return null;
  return (
    <div data-testid="churn-content">
      <Card className="p-5 mb-5 bg-rose-50 border-rose-200" data-testid="churn-headline">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white border border-rose-200 text-rose-700 flex items-center justify-center flex-none">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-slate-900">{d.count} failed payment{d.count === 1 ? "" : "s"}</div>
            <div className="text-sm text-slate-600 mt-0.5">
              At risk: <strong>{formatCents(d.total_at_risk_cents || 0)}</strong> · Stripe retries automatically — these need manual outreach.
            </div>
          </div>
        </div>
      </Card>

      {(d.items || []).length === 0 ? (
        <Card className="p-10 text-center"><div className="text-sm text-slate-500">No failed payments in the last 30 days.</div></Card>
      ) : (
        <div className="space-y-3">
          {d.items.map((f, i) => (
            <Card key={i} className="p-5" data-testid={`churn-row-${i}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0">
                  <div className="font-display text-base font-bold text-slate-900 truncate">{f.tenant_email || "Unknown tenant"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{formatCents(f.amount_cents)} · {f.failure_reason}</div>
                </div>
                <Link to="/payments" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">
                  Open payment <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <DraftMessage text={f.suggested_message} email={f.tenant_email}
                testid={`churn-msg-${i}`} kind="churn_recovery"
                subjectHint="Rent payment didn't go through" />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SmartInsightsTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = () => {
    setLoading(true);
    api.get("/revenue/smart-insights")
      .then((r) => setD(r.data))
      .catch(() => toast.error("Couldn't load insights"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);
  if (loading) return <Loader />;
  if (!d) return null;
  const urgencyTone = (u) => ({ high: "border-rose-300 bg-rose-50", med: "border-amber-300 bg-amber-50", low: "border-slate-200 bg-white" }[u] || "border-slate-200 bg-white");

  return (
    <div data-testid="insights-content">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Source: <strong className="text-slate-700">{d.source === "smart" ? "AI on your live data" : "Rule-based fallback"}</strong>
        </div>
        <button onClick={reload} data-testid="insights-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 px-2.5 py-1 rounded-md hover:bg-slate-50">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="grid gap-3">
        {(d.insights || []).map((i, idx) => (
          <Card key={idx} className={`p-5 border ${urgencyTone(i.urgency)}`} data-testid={`insight-${idx}`}>
            <div className="flex items-start gap-3">
              <div className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                i.urgency === "high" ? "bg-rose-600 text-white" :
                i.urgency === "med" ? "bg-amber-500 text-white" :
                "bg-slate-200 text-slate-700"
              }`}>{i.urgency || "low"}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-base font-bold text-slate-900">{i.title}</div>
                <div className="text-sm text-slate-600 mt-1">{i.detail}</div>
                {i.action && (i.route ? (
                  <Link to={i.route} data-testid={`insight-action-${idx}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline mt-2">
                    → {i.action}
                  </Link>
                ) : (
                  <div className="text-xs font-semibold text-emerald-700 mt-2">→ {i.action}</div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function DraftMessage({ text, email, phone, testid, kind = "revenue_engine", subjectHint }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setSent(true);
      toast.success("Message copied — paste it into your email or SMS.");
      setTimeout(() => setSent(false), 2000);
    } catch (_) { toast.error("Couldn't copy"); }
  };
  const sendNow = async () => {
    if (!email) return;
    if (sending) return;
    setSending(true);
    try {
      await api.post("/revenue/send-via-resend", {
        to_email: email,
        subject: subjectHint || "A quick note from your landlord",
        body: text,
        kind,
      });
      toast.success("Sent via Resend — they have it now.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't send. Try again or check your Resend key.";
      toast.error(String(detail));
    } finally { setSending(false); }
  };
  return (
    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid={testid}>
      <div className="text-xs text-slate-500 mb-1.5 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Suggested follow-up</div>
      <p className="text-sm text-slate-700 leading-relaxed">{text}</p>
      <div className="mt-2.5 flex items-center gap-2 flex-wrap">
        {email && (
          <button onClick={sendNow} disabled={sending} data-testid={`${testid}-send-now`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60">
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            {sending ? "Sending…" : "Send via Resend"}
          </button>
        )}
        <button onClick={copy} data-testid={`${testid}-copy`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          {sent ? "Copied" : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
        {email && (
          <a href={`mailto:${email}?body=${encodeURIComponent(text)}`} data-testid={`${testid}-email`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <Mail className="w-3 h-3" /> Open email app
          </a>
        )}
        {phone && (
          <a href={`sms:${phone}?body=${encodeURIComponent(text)}`} data-testid={`${testid}-sms`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700">
            <Send className="w-3 h-3" /> Text
          </a>
        )}
      </div>
    </div>
  );
}
